/**
 * src/pricingtable/PricingPageHeader2.jsx — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * WordPress-aligned pricing page header for the new app pricing flow.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * The current app pricing header was built around the legacy in-app pricing
 * table structure and does not match the live WordPress pricing page.
 *
 * The live WordPress pricing page header currently renders:
 *   • a compact centered title only
 *   • no subtitle
 *   • a simple Annual / Monthly segmented toggle
 *
 * This file intentionally mirrors that structure as closely as possible
 * within the React/MUI app while introducing a compact, enterprise-grade,
 * PLG-friendly Users control for the authenticated pricing page.
 *
 * IMPORTANT
 * ------------------------------------------------------------------
 * The Users control is intentionally minimal and stacked:
 *   • small label above
 *   • compact numeric input below
 *   • native keyboard editing still works
 *   • users can type, click, or use keyboard arrows
 *   • increment/decrement buttons are embedded in the field
 *
 * DESIGN GOALS
 * ------------------------------------------------------------------
 * 1. Match the current WordPress pricing intro structure.
 * 2. Keep the title compact and centered.
 * 3. Keep the toggle simple: Annual / Monthly only.
 * 4. Add a compact MUI V5+ Users control that does not overpower the toggle.
 * 5. Keep the toggle visually centered while placing Users on the left.
 * 6. Add logs so local testing is easy.
 */

import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";

import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

/* ------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------ */
const MIN_USERS = 1;
const MAX_USERS = 500;
const FALLBACK_USERS = 1;

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */
function clampUsers(value) {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) return FALLBACK_USERS;

  const rounded = Math.round(numeric);

  if (rounded < MIN_USERS) return MIN_USERS;
  if (rounded > MAX_USERS) return MAX_USERS;

  return rounded;
}

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */
const PricingPageHeader2 = ({
  context,
  seatCount,
  onSeatCountChange,
  pricingInterval,
  onIntervalChange,
  pricingPresentation,
}) => {
  const defaultInterval =
    pricingPresentation?.defaults?.interval === "monthly"
      ? "monthly"
      : "annual";

  const effectiveInterval =
    pricingInterval === "monthly" || pricingInterval === "annual"
      ? pricingInterval
      : defaultInterval;

  const resolvedSeatCount = clampUsers(seatCount ?? FALLBACK_USERS);

  /**
   * Local field state:
   * - allows temporary empty state while typing
   * - commits sanitized values back to parent on blur / buttons / Enter
   */
  const [inputValue, setInputValue] = useState(String(resolvedSeatCount));

  /**
   * The live WordPress page uses:
   *   "Pick the plan that fits your team."
   *
   * To keep the app aligned with the current public design, we use the
   * same title here for both contexts during this migration phase.
   */
  const title = useMemo(() => {
    return "Pick the plan that fits your team.";
  }, []);

  /* ----------------------------------------------------------------
   * Diagnostics
   * ---------------------------------------------------------------- */
  useEffect(() => {
    console.log("[PricingPageHeader2] Mounted:", {
      context,
      seatCount: resolvedSeatCount,
      pricingInterval,
      effectiveInterval,
      payloadLoaded: pricingPresentation?.loading === false,
      payloadPath: pricingPresentation?.payloadPath || null,
      usersControlRendered: true,
      usersControlVariant: "minimal_stacked_field",
    });
  }, []); // mount only

  useEffect(() => {
    console.log("[PricingPageHeader2] Props/state update:", {
      context,
      seatCount: resolvedSeatCount,
      inputValue,
      pricingInterval,
      effectiveInterval,
      payloadLoading: pricingPresentation?.loading,
      payloadError: pricingPresentation?.error || null,
      usersControlRendered: true,
      usersControlVariant: "minimal_stacked_field",
    });
  }, [
    context,
    resolvedSeatCount,
    inputValue,
    pricingInterval,
    effectiveInterval,
    pricingPresentation?.loading,
    pricingPresentation?.error,
  ]);

  /* ----------------------------------------------------------------
   * Keep local field in sync with external seatCount changes
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const nextValue = String(resolvedSeatCount);

    if (inputValue !== nextValue) {
      console.log("[PricingPageHeader2] Syncing local input from seatCount:", {
        previousInputValue: inputValue,
        nextInputValue: nextValue,
      });
      setInputValue(nextValue);
    }
  }, [resolvedSeatCount, inputValue]);

  /* ----------------------------------------------------------------
   * Handlers
   * ---------------------------------------------------------------- */
  const handleIntervalChangeLocal = useCallback(
    (_, nextInterval) => {
      if (!nextInterval) return;

      if (nextInterval !== "annual" && nextInterval !== "monthly") {
        console.warn(
          "[PricingPageHeader2] Ignoring invalid interval:",
          nextInterval,
        );
        return;
      }

      console.log("[PricingPageHeader2] Interval changed:", {
        previousInterval: effectiveInterval,
        nextInterval,
      });

      if (typeof onIntervalChange === "function") {
        onIntervalChange(nextInterval);
      }
    },
    [effectiveInterval, onIntervalChange],
  );

  const commitSeatCount = useCallback(
    (rawValue, source) => {
      const nextCount = clampUsers(rawValue);

      console.log("[PricingPageHeader2] commitSeatCount:", {
        source,
        rawValue,
        nextCount,
        previousSeatCount: resolvedSeatCount,
      });

      setInputValue(String(nextCount));

      if (
        typeof onSeatCountChange === "function" &&
        nextCount !== resolvedSeatCount
      ) {
        onSeatCountChange(nextCount);
      }
    },
    [onSeatCountChange, resolvedSeatCount],
  );

  const handleSeatInputChange = useCallback((event) => {
    const nextRaw = event.target.value;

    if (nextRaw === "") {
      console.log("[PricingPageHeader2] Users input temporarily cleared.");
      setInputValue("");
      return;
    }

    if (!/^\d+$/.test(nextRaw)) {
      console.warn("[PricingPageHeader2] Ignoring non-numeric seat input:", {
        nextRaw,
      });
      return;
    }

    setInputValue(nextRaw);
  }, []);

  const handleSeatInputBlur = useCallback(() => {
    commitSeatCount(
      inputValue === "" ? resolvedSeatCount : inputValue,
      "blur",
    );
  }, [commitSeatCount, inputValue, resolvedSeatCount]);

  const handleSeatInputKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commitSeatCount(
          inputValue === "" ? resolvedSeatCount : inputValue,
          "enter",
        );
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        commitSeatCount(
          (inputValue === "" ? resolvedSeatCount : Number(inputValue)) + 1,
          "arrow_up",
        );
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        commitSeatCount(
          (inputValue === "" ? resolvedSeatCount : Number(inputValue)) - 1,
          "arrow_down",
        );
      }
    },
    [commitSeatCount, inputValue, resolvedSeatCount],
  );

  const handleDecrement = useCallback(() => {
    commitSeatCount(resolvedSeatCount - 1, "decrement_button");
  }, [commitSeatCount, resolvedSeatCount]);

  const handleIncrement = useCallback(() => {
    commitSeatCount(resolvedSeatCount + 1, "increment_button");
  }, [commitSeatCount, resolvedSeatCount]);

  /* ----------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  return (
    <Box
      sx={{
        mb: 4,
      }}
    >
      {/* ------------------------------------------------------------
       * Compact intro block
       * Matches the current WordPress pricing intro:
       *   - centered
       *   - title only
       *   - no subtitle
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          maxWidth: "900px",
          margin: "0 auto",
          textAlign: "center",
          paddingTop: {
            xs: "12px",
            sm: "16px",
            md: "20px",
          },
          paddingBottom: {
            xs: "4px",
            sm: "6px",
            md: "6px",
          },
        }}
      >
        <Typography
          component="h1"
          sx={{
            margin: 0,
            marginBottom: "8px",
            textAlign: "center",
            color: "#0F1A2A",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontWeight: 400,
            letterSpacing: "-0.025em",
            fontSize: {
              xs: "34px",
              sm: "40px",
              md: "56px",
              lg: "64px",
            },
            lineHeight: 1.04,
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* ------------------------------------------------------------
       * Pricing controls row
       *
       * Desktop:
       *   - left: compact Users control
       *   - center: toggle
       *   - right: spacer for visual centering
       *
       * Mobile:
       *   - stacked vertically
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(140px, 180px) auto minmax(140px, 180px)",
          },
          alignItems: {
            xs: "stretch",
            md: "end",
          },
          columnGap: {
            xs: 0,
            md: 2,
          },
          rowGap: {
            xs: 1.5,
            md: 0,
          },
          mt: "20px",
          mb: "18px",
        }}
      >
        {/* --------------------------------------------------------
         * Left column: minimal stacked Users field
         * ------------------------------------------------------ */}
        <Box
          sx={{
            display: "flex",
            justifyContent: {
              xs: "center",
              md: "flex-start",
            },
            alignItems: "flex-end",
          }}
        >
			<Box
			  sx={{
			    display: "flex",
			    flexDirection: "column",
			    alignItems: {
			      xs: "center",
			      md: "flex-start",
			    },
			    width: "fit-content",
			  }}
			>
            <Typography
              component="label"
              htmlFor="pricing-users-input"
              sx={{
                mb: "6px",
                fontFamily:
                  "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                color: "#64748b",
                lineHeight: 1.2,
                letterSpacing: 0,
              }}
            >
              Number of Users
            </Typography>

            <OutlinedInput
              id="pricing-users-input"
              type="text"
              value={inputValue}
              onChange={handleSeatInputChange}
              onBlur={handleSeatInputBlur}
              onKeyDown={handleSeatInputKeyDown}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                "aria-label": "Number of users",
                min: MIN_USERS,
                step: 1,
                style: {
                  textAlign: "center",
                  padding: "0 2px",
                  width: "34px",
                  fontFamily:
                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "40px",
                },
              }}
              startAdornment={
                <InputAdornment position="start" sx={{ mr: 0 }}>
                  <IconButton
                    aria-label="Decrease users"
                    onClick={handleDecrement}
                    edge="start"
                    size="small"
                    sx={{
                      color: "#475569",
                      width: 24,
                      height: 24,
                      "&:hover": {
                        backgroundColor: "rgba(15,26,42,.05)",
                        color: "#0F1A2A",
                      },
                    }}
                  >
                    <RemoveIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end" sx={{ ml: 0 }}>
                  <IconButton
                    aria-label="Increase users"
                    onClick={handleIncrement}
                    edge="end"
                    size="small"
                    sx={{
                      color: "#475569",
                      width: 24,
                      height: 24,
                      "&:hover": {
                        backgroundColor: "rgba(15,26,42,.05)",
                        color: "#0F1A2A",
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              }
              sx={{
                height: "40px",
                width: "100%",
                background: "#FFFFFF",
                borderRadius: "12px",
                boxShadow: "0 1px 2px rgba(15,26,42,.04)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(15,26,42,.12)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(15,26,42,.22)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4864ff",
                  borderWidth: "1px",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 3px rgba(72,100,255,.15)",
                },
              }}
            />
          </Box>
        </Box>

        {/* --------------------------------------------------------
         * Center column: Billing interval toggle
         * Matches current WordPress behavior:
         *   - centered segmented control
         *   - Annual
         *   - Monthly
         *   - no "Save X%" text in the pills
         * ------------------------------------------------------ */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ToggleButtonGroup
            value={effectiveInterval}
            exclusive
            onChange={handleIntervalChangeLocal}
            aria-label="Billing interval"
            sx={{
              gap: "4px",
              flexWrap: "nowrap",
              width: "fit-content",
              maxWidth: "100%",
              padding: "4px",
              borderRadius: "999px",
              background: "rgba(255,255,255,.72)",
              border: "1px solid rgba(15,26,42,.08)",
              boxShadow: "0 8px 18px rgba(15,26,42,.06)",
              backdropFilter: "saturate(140%) blur(6px)",
              WebkitBackdropFilter: "saturate(140%) blur(6px)",
              "& .MuiToggleButtonGroup-grouped": {
                appearance: "none",
                border: "none",
                background: "transparent",
                padding: "10px 18px",
                minWidth: "110px",
                borderRadius: "999px !important",
                fontFamily:
                  "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                color: "#475569",
                cursor: "pointer",
                lineHeight: 1,
                textTransform: "none",
                transition:
                  "color .15s ease, background-color .15s ease, box-shadow .15s ease, transform .12s ease",
              },
              "& .MuiToggleButtonGroup-grouped:hover": {
                background: "rgba(15,26,42,.05)",
                color: "#0F1A2A",
              },
              "& .MuiToggleButtonGroup-grouped.Mui-selected": {
                color: "#FFFFFF",
                background: "#4864ff",
                boxShadow: "0 6px 16px rgba(17,24,39,.14)",
              },
              "& .MuiToggleButtonGroup-grouped.Mui-selected:hover": {
                background: "#3648d4",
              },
              "& .MuiToggleButtonGroup-grouped:focus-visible": {
                outline: "none",
                boxShadow: "0 0 0 3px rgba(72,100,255,.35)",
              },
            }}
          >
            <ToggleButton value="annual" aria-label="Annual billing">
              Annual
            </ToggleButton>

            <ToggleButton value="monthly" aria-label="Monthly billing">
              Monthly
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* --------------------------------------------------------
         * Right spacer to preserve true visual centering of toggle
         * ------------------------------------------------------ */}
        <Box
          aria-hidden="true"
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
            minHeight: "40px",
          }}
        />
      </Box>
    </Box>
  );
};

PricingPageHeader2.propTypes = {
  context: PropTypes.oneOf(["public", "account"]).isRequired,
  seatCount: PropTypes.number,
  onSeatCountChange: PropTypes.func,
  pricingInterval: PropTypes.oneOf(["monthly", "annual"]),
  onIntervalChange: PropTypes.func,
  pricingPresentation: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.string,
    payloadPath: PropTypes.string,
    target: PropTypes.string,
    pricingEnv: PropTypes.string,
    defaults: PropTypes.shape({
      interval: PropTypes.string,
    }),
    plans: PropTypes.array,
    compare: PropTypes.array,
  }),
};

export default PricingPageHeader2;